import {Transaction, File, Config} from 'olympe';
import {getLogger} from '@olympeio/core';
import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import Typography from '@mui/material/Typography';
import {Box, Button, CircularProgress} from '@mui/material';
import {MAX_FILE_SIZE} from '../../../utils/constants';
import UserController from '../../../controllers/UserController';
import {removeAccents} from '../../../utils/helpers';

/**
 * @param {PRFileUpload.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function PRFileUpload(props) {
    // Create logger
    const logger = getLogger('PRFileUpload');

    // Helper function to upload the file in DC
    const uploadFile = (file, fileName, onUploadDone) => {
        // Init transaction
        const transaction = new Transaction();

        // Read file
        const reader = new FileReader();
        reader.onloadend = () => {
            // Create file for DC
            const tag = File.createFromContent(transaction, fileName, reader['result'], file.type);
            transaction.persist(tag, props.persistFiles);

            // Execute transaction
            transaction.execute().then(() => {
                logger.debug(`Uploaded file: ${tag}`);
                onUploadDone(tag);
            }).catch((message) => {
                logger.warn(`Failed to create file ${fileName} in DC: ${message}`);
            });
        };

        reader.readAsArrayBuffer(file);
    };

    const getUserFullName = () => {
        return localStorage.getItem('userFullName') || '';
    };

    // State for the uploaded filename
    const [uploadedFilename, setUploadedFilename] = useState('');

    // State for the loading spinner when we upload a file
    const [loading, setLoading] = useState(false);

    const inputRef = useRef();
    const resetInput = () => {
        setUploadedFilename('');
        inputRef.current.value = '';
    };

    useEffect(() => {
        if (props.triggerClearFileName !== null) {
            resetInput();
        }
    }, [props.triggerClearFileName]);

    return (
        <Box className={'pr-editor-attachments-file-upload-container'} sx={props.fixedSize ? {width: '340px'} : {}}>
            <Button
                startIcon={<AddIcon />}
                component='label'
                className={'pr-editor-attachments-file-upload-btn'}
                variant={'outlined'}
                disabled={props?.disabled}
                data-cy={'add_file_optional_doc'}
            >
                <Typography className={'pr-editor-attachments-file-upload-btn-label'} variant='body2'>Choose File</Typography>
                <input
                    // Hidden file input
                    type='file'
                    multiple={false}
                    accept='*'
                    hidden
                    ref={inputRef}
                    // Events
                    onChange={(event) => {
                        if (event.target.files.length > 0) {
                            const selectableMaxFileSize = 1024 * 1024 * MAX_FILE_SIZE.get(Config.getParameter('ENV'));
                            const file = event.target.files[0];
                            const fileSize = file.size;

                            // Do not allow users to upload files higher size than 50 MB
                            if (fileSize > selectableMaxFileSize) {
                                alert(`Please upload a file smaller than ${MAX_FILE_SIZE.get(Config.getParameter('ENV'))} MB`);
                                return resetInput();
                            }
                            const fileName = removeAccents(file.name);
                            setUploadedFilename(fileName.length <= 30 ? fileName : `${fileName.substring(0, 27)}...`);
                            setLoading(true);
                            uploadFile(
                                file,
                                fileName,
                                (tag) => {
                                    setLoading(false);
                                    props.onFileUploaded(
                                        {
                                            tag: tag,
                                            name: fileName,
                                            topic: 'Specification',
                                            user: getUserFullName(),
                                            date: Date.now(),
                                            userId: UserController.getCurrentUserName('CAADAPAAM'),
                                        }, resetInput);
                                },
                            );
                        }
                    }}
                />
            </Button>
            {loading && <CircularProgress size={20}/>}
            <Typography
                className={'pr-editor-attachments-file-upload-text'}
            >{uploadedFilename}</Typography>
        </Box>
    );
}

PRFileUpload.propTypes = {
    onFileUploaded: PropTypes.func,
    persistFiles: PropTypes.bool,
    disabled: PropTypes.bool,
    triggerClearFileName: PropTypes.bool,
    fixedSize: PropTypes.bool,
};
